// src/pages/admin/AdminTestimonials.jsx
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import {
  getTestimonials,
  addTestimonial,
  updateTestimonial,
  deleteTestimonial,
} from "../../services/firestoreService";
import Modal from "../../components/common/Modal";
import ImageUploader from "../../components/common/ImageUploader";
import Spinner from "../../components/common/Spinner";
import toast from "react-hot-toast";
import { format } from "date-fns";
import { FiPlus, FiEdit2, FiTrash2, FiStar, FiUser } from "react-icons/fi";

export default function AdminTestimonials() {
  const [testimonials, setTestimonials] = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [modalOpen,    setModalOpen]    = useState(false);
  const [editing,      setEditing]      = useState(null);
  const [rating,       setRating]       = useState(5);
  const [hoverRating,  setHoverRating]  = useState(0);
  const [saving,       setSaving]       = useState(false);
  const [image,        setImage]        = useState(""); // optional customer photo

  const { register, handleSubmit, reset, formState: { errors } } = useForm();

  const load = async () => {
    const t = await getTestimonials();
    setTestimonials(t);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const openAdd = () => {
    setEditing(null);
    setRating(5);
    setImage("");
    reset({ name: "", designation: "", comment: "" });
    setModalOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setRating(item.star || 5);
    setImage(item.image || "");
    reset({
      name: item.name || "",
      designation: item.designation || "",
      comment: item.comment || "",
    });
    setModalOpen(true);
  };

  const onSubmit = async (data) => {
    setSaving(true);
    try {
      const payload = { ...data, star: rating, image };
      if (editing) {
        await updateTestimonial(editing.id, payload);
        toast.success("Testimonial updated");
      } else {
        await addTestimonial(payload);
        toast.success("Testimonial added");
      }
      setModalOpen(false);
      load();
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete testimonial from "${name}"?`)) return;
    await deleteTestimonial(id);
    toast.success("Testimonial deleted");
    setTestimonials((prev) => prev.filter((t) => t.id !== id));
  };

  const StarDisplay = ({ count }) => (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <FiStar
          key={n}
          className={`w-4 h-4 ${n <= count ? "fill-yellow-400 text-yellow-400" : "text-gray-300"}`}
        />
      ))}
    </div>
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Testimonials</h1>
        <button onClick={openAdd} className="btn-primary text-sm sm:text-md  flex items-center gap-2">
          <FiPlus className="w-4 h-4" /> New Testimonial
        </button>
      </div>

      {loading ? <Spinner center /> : (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {["Name", "Designation", "Comment", "Rating", "Date", "Actions"].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide whitespace-nowrap">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {testimonials.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-12 text-gray-500">
                    No testimonials yet. Add your first one!
                  </td>
                </tr>
              ) : (
                testimonials.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 flex-shrink-0 overflow-hidden">
                          {item.image
                            ? <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                            : <FiUser className="w-4 h-4" />}
                        </div>
                        <span className="font-medium text-gray-900">{item.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{item.designation || "—"}</td>
                    <td className="px-4 py-3 text-gray-700 max-w-sm">
                      <p className="truncate">{item.comment}</p>
                    </td>
                    <td className="px-4 py-3"><StarDisplay count={item.star || 0} /></td>
                    <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                      {item.createdAt?.toDate ? format(item.createdAt.toDate(), "dd MMM yyyy") : "—"}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <button onClick={() => openEdit(item)} className="p-1.5 rounded-lg hover:bg-primary-50 text-primary-600 transition-colors">
                          <FiEdit2 className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleDelete(item.id, item.name)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-500 transition-colors">
                          <FiTrash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          </div>
        </div>
      )}

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title={editing ? "Edit Testimonial" : "New Testimonial"}>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <label className="label">Customer Photo (optional)</label>
            <ImageUploader
              maxImages={1}
              onUpload={(imgs) => setImage(imgs[0] || "")}
              existingImages={image ? [image] : []}
            />
            <p className="text-xs text-gray-400 mt-1">
              Leave empty to show a default avatar on the site.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Name *</label>
              <input
                {...register("name", { required: "Name is required" })}
                className={`input ${errors.name ? "border-red-400" : ""}`}
                placeholder="Customer name"
              />
              {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name.message}</p>}
            </div>
            <div>
              <label className="label">Designation</label>
              <input
                {...register("designation")}
                className="input"
                placeholder="e.g. Farmer, Hyderabad"
              />
            </div>
          </div>

          <div>
            <label className="label">Comment *</label>
            <textarea
              {...register("comment", { required: "Comment is required" })}
              rows={4}
              className={`input resize-none ${errors.comment ? "border-red-400" : ""}`}
              placeholder="What did the customer say?"
            />
            {errors.comment && <p className="text-xs text-red-500 mt-1">{errors.comment.message}</p>}
          </div>

          <div>
            <label className="label">Rating *</label>
            <div className="flex items-center gap-1">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  type="button"
                  key={n}
                  onClick={() => setRating(n)}
                  onMouseEnter={() => setHoverRating(n)}
                  onMouseLeave={() => setHoverRating(0)}
                  className="p-0.5"
                >
                  <FiStar
                    className={`w-7 h-7 transition-colors ${
                      n <= (hoverRating || rating)
                        ? "fill-yellow-400 text-yellow-400"
                        : "text-gray-300"
                    }`}
                  />
                </button>
              ))}
              <span className="ml-2 text-sm text-gray-500">{rating} / 5</span>
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <button type="submit" disabled={saving} className="btn-primary flex-1 flex items-center justify-center gap-2">
              {saving ? <><Spinner size="sm" /> Saving...</> : editing ? "Update Testimonial" : "Add Testimonial"}
            </button>
            <button type="button" onClick={() => setModalOpen(false)} className="btn-secondary">
              Cancel
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}